import { createId } from './id'
import { createSelector } from './selector-utils'

/**
 * @param {{
 *   rootKey: string
 *   idPrefix?: string
 *   requiredProps?: string[]
 *   sortFn?: (a: any, b: any) => number
 *   mapFn?: (any) => any
 * }} options
 * @returns {{
 *   actionAdd: (initial: any, preGeneratedId?: string) => any
 *   actionRemove: (id: string) => any
 *   actionUpdate: (id: string, updates: any) => any
 *   selectRaw: (caseData: any) => { [id: string]: any }
 *   selectById: (caseData: any) => { [id: string]: { id: string } & any }
 *   select: (caseData: any) => ({ id: string } & any)[]
 * }}
 */
export const createCrudProp = ({
  rootKey,
  idPrefix,
  requiredProps,
  sortFn,
  mapFn,
}) => {
  /**
   * @param {any} initial The initial values to apply
   * @param {string} [preGeneratedId] Optionally supply an ID generated outside.
   *   This is used by some of the speech actions to enable undo. So they can
   *   know which dose to remove if you cancel.
   * @returns {any} The changes to apply to the case.
   */
  const actionAdd = (initial, preGeneratedId) => {
    const id = preGeneratedId || createId(idPrefix || rootKey)
    return actionUpdate(id, initial)
  }

  /**
   * @param {string} id The id
   * @returns {any} The changes to apply to the case.
   */
  const actionRemove = id => ({
    [`${rootKey}.${id}`]: null,
  })

  /**
   * @param {string} id The id
   * @param {any} updates The updates to apply.
   * @returns {any} The changes to apply to the case.
   */
  const actionUpdate = (id, updates) => {
    const changes = {}
    for (const key in updates) {
      changes[`${rootKey}.${id}.${key}`] = updates[key]
    }
    return changes
  }

  const empty = {}
  const selectRaw = caseData => caseData[rootKey] || empty

  const select = createSelector(selectRaw, raw => {
    const toReturn = []
    for (const id in raw) {
      const data = raw[id]
      if (
        requiredProps &&
        !requiredProps.every(prop => data.hasOwnProperty(prop))
      ) {
        continue
      }
      toReturn.push({ id, ...raw[id] })
    }
    if (sortFn) {
      toReturn.sort(sortFn)
    }
    return mapFn ? toReturn.map(mapFn) : toReturn
  })

  // same as select but returns an object with the id as the key
  // different from selectRaw in that it returns the full object
  // after being processed by mapFn / sortFn.
  const selectById = createSelector(select, items =>
    items.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  )

  return {
    actionAdd,
    actionRemove,
    actionUpdate,
    selectRaw,
    selectById,
    select,
  }
}
